import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  
} from "react";
import { Link } from "react-router-dom";
export function ResponsiveDataTable({
  data = [],
  columns = [], // e.g, [{ key, label, mobileVisible: boolean }]
  autoScroll = true, // auto load more on scroll bottom
  onLoadMore, // function() => void   (optional for parent paging)
  hasMore = false, // parent indicates if more data available
  pageSize = 50, // number of rows to load per page
  title = "BP React Table",
  subtitle = "BP-react demo",
  renderOptions, // action buttons
  optionsHeader = "Options", // label for the actions header
  optionsMobileVisible = false, // HIDE options column on mobile; render in mobile details instead
  initHide = false, // initially hide certain columns (non-mobileVisible ones)
  displayAllRows = false, // show balance columns when true
  loadingClickableColumn = null, // key of the column which triggers loading state when clicked
  searchKey = null,
  loadBalanceClick,
  serverSearch = false,
  onServerSearch,
  searchResetKey = null,
  loadingMore = false,
  serverSearchPending = false,
  showTotal = false,
  totalBalance = 0,
  totalClientPL = 0,
  totalCreditGiven = 0,
  userLinkClick
}) {
  const [openRowId, setOpenRowId] = useState(null);
  const [sortKey, setSortKey] = useState(columns[0]?.key || "");
  const [sortDir, setSortDir] = useState("asc");
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [visibleRowContainer, setVisibleRowContainer] = useState([]);
  //const [displayAllRows, setDisplayAllRows] = useState(rowLoading);
  const [searchQuery, setSearchQuery] = useState("");

  const serverSearchTimerRef = useRef(null);

  // console.log("columns", columns);
  // console.log("data", data);

  const formatTruncate = (value) => {
    let num = Math.round(value);
    const truncated = Math.floor(num * 1000) / 1000;

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(truncated);
  };

  const RowLoader = ({ row, colkey }) => {
    const hasValue = visibleRowContainer.includes(row._id);

    const revealRow = (e) => {
      if (e) e.stopPropagation();
      setVisibleRowContainer((prev) =>
        prev.includes(row._id)
          ? prev.filter((id) => id !== row._id)
          : [...prev, row._id]
      );
    };

    const linkClick = (e) => {
      userLinkClick();
    };

    return (
      <>
        {row.user_role == 8 ? (
          <strong
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                revealRow(e);
              }
            }}
            style={{
              color: "#000",
              fontSize: "17px",
              fontWeight: "bold",
              cursor: hasValue ? "default" : "default"
            }}
          >
            {row[colkey]}
          </strong>
        ) : (
          <Link
            to={`/agent/accounts/chart/${row._id}`}
            onClick={() => {
              linkClick();
            }}
            style={{
              color: "#00b181",
              fontSize: "17px",
              fontWeight: "bold",
              cursor: "pointer"
            }}
          >
            {row[colkey]}
          </Link>
        )}

        {!displayAllRows && (
          <button
            type="button"
            onClick={revealRow}
            className="btn"
            style={{ float: "right", background: "#efefef" }}
            aria-label={
              hasValue ? "Hide account details" : "Show account details"
            }
          >
            <i className="fa fa-info-circle"></i>
          </button>
        )}
      </>
    );
  };

  useEffect(() => {
    setSearchQuery("");
    if (serverSearchTimerRef.current) {
      clearTimeout(serverSearchTimerRef.current);
    }
  }, [searchResetKey]);

  useEffect(() => {
    return () => {
      if (serverSearchTimerRef.current) {
        clearTimeout(serverSearchTimerRef.current);
      }
    };
  }, []);

  // Reset visible window on sort changes or when pageSize changes
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [pageSize]);

  useEffect(() => {
    setOpenRowId(null);
    setVisibleCount(pageSize);
  }, [sortKey, sortDir, pageSize]);

  // Normalize values for consistent sorting
  const normalize = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value.toLowerCase();
    if (typeof value === "number") return String(value);
    try {
      return JSON.stringify(value).toLowerCase();
    } catch {
      return String(value).toLowerCase();
    }
  };

  // Sorting (no filtering here; parent supplies already-filtered data)
  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const av = normalize(a[sortKey]);
      const bv = normalize(b[sortKey]);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const usingParentPaging = typeof onLoadMore === "function";
  const visibleRows = useMemo(
    () => (usingParentPaging ? sorted : sorted.slice(0, visibleCount)),
    [sorted, usingParentPaging, visibleCount]
  );

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  /** Match EasyGrid: even index → #f7f7f7, odd → #ffffff */
  const getStripeClass = (index) =>
    //index % 2 === 0 ? "dt-stripe-muted" : "dt-stripe-white";
    index % 2 === 0 ? "odd" : "even";

  // IntersectionObserver for auto load more
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!autoScroll || loadingMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (usingParentPaging) {
          if (hasMore && onLoadMore) onLoadMore();
        } else {
          setVisibleCount((cnt) =>
            cnt < sorted.length ? Math.min(cnt + pageSize, sorted.length) : cnt
          );
        }
      },
      { rootMargin: "300px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    autoScroll,
    usingParentPaging,
    hasMore,
    onLoadMore,
    sorted.length,
    pageSize,
    loadingMore
  ]);

  const Arrow = ({ active, dir }) => (
    <span
      className={`ml-1 ${active ? "opacity-100" : "opacity-50"}`}
      style={{
        display: "inline-flex",
        transform: dir === "desc" && active ? "rotate(180deg)" : "none"
      }}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 15 12 9 18 15"></polyline>
      </svg>
    </span>
  );

  const mobileVisibleCount = useMemo(
    () => columns.filter((c) => c.mobileVisible).length,
    [columns]
  );

  return (
    <div style={{ marginLeft: "15px", marginRight: "25px" }}>
      <h1 className="h3 mb-1">{title}</h1>
      <p className="text-muted mb-3">{subtitle}</p>

      <div className="table-responsive shadow-sm rounded datatable-easygrid-stripe">
        <style>{`
          .datatable-easygrid-stripe table.table tbody tr.dt-stripe-muted > td {
            background-color: #f7f7f7 !important;
            background-image: none !important;
          }
          .datatable-easygrid-stripe table.table tbody tr.dt-stripe-white > td {
            background-color: #ffffff !important;
            background-image: none !important;
          }
          .datatable-easygrid-stripe table.table tbody tr.table-active > td {
            background-color: rgba(0, 0, 0, 0.075) !important;
          }
          .datatable-easygrid-stripe table.table tbody tr.dt-mobile-detail-row > td {
            background-color: #ffffff !important;
            background-image: none !important;
          }
          .datatable-easygrid-stripe .dt-mobile-options-line {
            display: flex;
            flex-direction: row;
            align-items: center;
            flex-wrap: nowrap;
            gap: 8px;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .datatable-easygrid-stripe .dt-mobile-options-line .dt-mobile-options-label {
            flex-shrink: 0;
            font-weight: bold;
            margin: 0;
            white-space: nowrap;
          }
          .datatable-easygrid-stripe .dt-mobile-options-line .dt-mobile-options-actions {
            display: inline-flex;
            align-items: center;
            flex-wrap: nowrap;
            flex: 1 1 auto;
            min-width: 0;
          }
          .datatable-easygrid-stripe .dt-mobile-options-line .dt-mobile-options-actions > * {
            display: inline-flex;
            align-items: center;
            flex-wrap: nowrap;
          }
         
          table.dataTable.table > tbody > tr {
            background-color: none !important;
        }

          .datatable-easygrid-stripe table.table tbody tr.odd > td {
            background-color: #ffffff !important;
        }
        
        .datatable-easygrid-stripe table.table tbody tr.even > td {
            background-color: #f9f9f9 !important;
        }
      
        `}</style>
        <div className="d-flex justify-content-end mb-2">
          {searchKey && (
            <div style={{ display: "grid", placeItems: "center" }}>
              <div className="site_family" style={{ textAlign: "right" }}>
                Search:{""}
              </div>
              <input
                type="text"
                style={{ border: "1px solid #e4e7ea" }}
                name="search"
                className="site_family"
                placeholder=""
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (!serverSearch || typeof onServerSearch !== "function") {
                    return;
                  }
                  if (serverSearchTimerRef.current) {
                    clearTimeout(serverSearchTimerRef.current);
                  }
                  serverSearchTimerRef.current = setTimeout(() => {
                    onServerSearch(value.trim());
                  }, 300);
                }}
              />
              {serverSearchPending ? (
                <div className="site_family" style={{ fontSize: "12px" }}>
                  Searching...
                </div>
              ) : null}
            </div>
          )}
        </div>
        <table className="display table table-bordered tbl-datatable1 dataTable no-footer dtr-inline">
          <thead className="thead-dark">
            <tr style={{ background: "#04b181" }}>
              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  color: "#000"
                }}
                rowSpan="1"
                colSpan="1"
              >
                <span
                  style={{ float: "left", display: showTotal ? "none" : "" }}
                >
                  <a
                    style={{
                      padding: "6px",
                      display: displayAllRows ? "none" : ""
                    }}
                    type="button"
                    className="btn btn-warning"
                    onClick={loadBalanceClick}
                  >
                    <strong className="site_family">Load Balance</strong>
                  </a>
                </span>
                <span
                  style={{
                    display: "none",
                    top: "4px",
                    left: "10px"
                  }}
                >
                  <img src="../../../assets/images/preloader.gif" />
                </span>
                <span
                  class="site_family"
                  style={{ display: showTotal ? "" : "none" }}
                >
                  Total
                </span>
              </td>

              <td
                data-orderable="false"
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                rowSpan="1"
                colSpan="1"
              >
                &nbsp;
              </td>
              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                rowSpan="1"
                colSpan="1"
              >
                <span
                  style={{ display: showTotal ? "" : "none" }}
                  className="site_family"
                >
                  {formatTruncate(totalCreditGiven)}
                </span>
              </td>

              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                rowSpan="1"
                colSpan="1"
                className={`${
                  columns[3].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
              >
                <span
                  style={{ display: showTotal ? "" : "none" }}
                  className="site_family"
                >
                  {formatTruncate(totalBalance)}
                </span>
              </td>
              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                className={`${
                  columns[4].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
                rowSpan="1"
                colSpan="1"
              >
                <span
                  style={{ display: showTotal ? "" : "none" }}
                  className="site_family"
                >
                  {formatTruncate(totalClientPL)}
                </span>
              </td>

              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                className={`${
                  columns[5].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
                rowSpan="1"
                colSpan="1"
              >
                &nbsp;
              </td>

              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                className={`${
                  columns[6].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
                rowSpan="1"
                colSpan="1"
              ></td>

              <td
                style={{
                  border: "1px solid #c8ced3",
                  fontWeight: "bold",
                  textAlign: "left",
                  color: "#000"
                }}
                className={`${
                  columns[7].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
                rowSpan="1"
                colSpan="1"
              ></td>
              <td
                className={`${
                  columns[7].mobileVisible
                    ? "td_css sorting_disabled"
                    : "td_css sorting_disabled d-none d-md-table-cell"
                }`}
                rowSpan="1"
                colSpan="1"
              ></td>
            </tr>

            <tr style={{ background: "whitesmoke" }} role="row">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`${
                    col.mobileVisible
                      ? "td_css sorting_disabled site_family"
                      : "td_css sorting_disabled d-none d-md-table-cell site_family"
                  }`}
                  scope="col"
                  style={{
                    fontWeight: "bold",
                    padding: "10px 18px",
                    borderBottom: "1px solid #111"
                  }}
                >
                  {col.label}
                  {/*onClick={() => onSort(col.key)}*/}
                  {/*<Arrow active={sortKey === col.key} dir={sortKey === col.key ? sortDir : 'asc'} />*/}
                </td>
              ))}
              <td
                className="d-none d-md-table-cell site_family"
                style={{
                  padding: "10px 18px",
                  borderBottom: "1px solid #111"
                }}
                scope="col"
              >
                <span className="text-black site_family">{optionsHeader}</span>
              </td>
            </tr>
          </thead>

          <tbody>
            {(() => {
              let stripeIdx = 0;
              return visibleRows.map((row, idx) => {
                if (searchKey && !serverSearch) {
                  if (
                    !String(row[searchKey])
                      .toLowerCase()
                      .match(searchQuery.toLowerCase())
                  ) {
                    return null; // skip if it doesn't match
                  }
                }
                const stripeClass = getStripeClass(stripeIdx);
                stripeIdx += 1;
                const rowId = row.id ?? idx;
                const isOpen = openRowId === rowId;
                return (
                  <React.Fragment key={rowId}>
                    <tr
                      className={`${stripeClass}${
                        isOpen ? "table-active" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={`${
                            col.mobileVisible
                              ? "site_family"
                              : "d-none d-md-table-cell site_family"
                          } ${
                            col.mobileVisible ? "site_family" : "site_family"
                          }`}
                        >
                          {loadingClickableColumn === col.key ? (
                            <RowLoader row={row} colkey={col.key} />
                          ) : col.options ? (
                            col.options[row[col.key]]
                          ) : !displayAllRows &&
                            !visibleRowContainer.includes(row._id) ? (
                            "-"
                          ) : (
                            formatTruncate(row[col.key])
                          )}
                        </td>
                      ))}

                      <td
                        className="d-none d-md-table-cell"
                        onClick={(e) => e.stopPropagation()} // don't toggle row when clicking actions
                      >
                        {typeof renderOptions === "function" ? (
                          renderOptions(row)
                        ) : (
                          <div className="btn-group btn-group-sm" role="group">
                            <a
                              className="btn btn-warning"
                              href="javascript:void(0)"
                            >
                              <strong className="site_family">C</strong>
                            </a>

                            <a className="btn btn-primary" target="_blank">
                              <i
                                className="fa fa-pencil"
                                aria-hidden="true"
                              ></i>
                            </a>
                            <a
                              className="btn btn-info"
                              href="javascript:void(0);"
                              onClick="openUserladgerPopup('MTA4NzU=')"
                            >
                              <strong className="site_family">L</strong>
                            </a>
                            {/*
                          <button type="button" className="btn btn-info">View</button>
                          <button type="button" className="btn btn-primary">Edit</button>
                          <button type="button" className="btn btn-danger">Delete</button>
                          */}
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Mobile-only details row:
                      - shows columns hidden on mobile
                      - ALWAYS shows Options block here on mobile */}
                    <tr
                      className={`d-md-none dt-mobile-detail-row ${
                        !displayAllRows &&
                        !visibleRowContainer.includes(row._id)
                          ? "d-none"
                          : ""
                      }`}
                    >
                      <td colSpan={mobileVisibleCount || 1}>
                        <div className="p-2">
                          {columns
                            .filter((c) => !c.mobileVisible)
                            .map((c) => (
                              <Detail
                                key={c.key}
                                label={c.label}
                                value={row[c.key]}
                              />
                            ))}

                          {/* Options placed in mobile details */}
                          <div
                            className="mt-2 dt-mobile-options-line"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span
                              className="dt-mobile-options-label"
                              style={{ fontWeight: "normal" }}
                            >
                              {optionsHeader}
                            </span>
                            <div className="dt-mobile-options-actions">
                              {typeof renderOptions === "function" ? (
                                renderOptions(row)
                              ) : (
                                <div
                                  className="btn-group btn-group-sm"
                                  role="group"
                                >
                                  <a
                                    className="btn btn-warning"
                                    href="javascript:void(0)"
                                  >
                                    <strong className="site_family">C</strong>
                                  </a>

                                  <a
                                    className="btn btn-primary"
                                    target="_blank"
                                  >
                                    <i
                                      className="fa fa-pencil"
                                      aria-hidden="true"
                                    ></i>
                                  </a>
                                  <a
                                    className="btn btn-info"
                                    href="javascript:void(0);"
                                    onClick="openUserladgerPopup('MTA4NzU=')"
                                  >
                                    <strong className="site_family">L</strong>
                                  </a>
                                  {/*
                              <button type="button" className="btn btn-primary">Edit</button>
                              <button type="button" className="btn btn-danger">Delete</button>
                          */}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              });
            })()}
            {visibleRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="text-center text-muted site_family"
                >
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Fallback Load More button */}
      {(!autoScroll ||
        (usingParentPaging ? hasMore : visibleCount < sorted.length)) && (
        <div className="text-center mt-3">
          <button
            className="btn btn-outline-primary"
            onClick={() => {
              if (usingParentPaging) {
                if (hasMore && onLoadMore) onLoadMore();
              } else {
                setVisibleCount((v) => Math.min(v + pageSize, sorted.length));
              }
            }}
          >
            Load more
          </button>
        </div>
      )}

      {loadingMore ? (
        <div className="text-center site_family py-2">Loading more...</div>
      ) : null}

      <div ref={sentinelRef} style={{ height: 8 }} />
    </div>
  );
}

function formatTruncate(value) {
  let num = Math.round(value);
  const truncated = Math.floor(num * 1000) / 1000;

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  }).format(truncated);
}

function Detail({ label, value }) {
  return (
    <div>
      <span>
        <i
          className="fa fa-circle "
          aria-hidden="true"
          style={{ fontSize: "5px", color: "#000000" }}
        ></i>{" "}
        <span className="site_family">{label}</span>
      </span>
      <span className="text-black site_family" style={{ paddingLeft: "5px" }}>
        {formatTruncate(Math.round(value))}
      </span>
    </div>
  );
}
